
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ModelStatus = "loading" | "ready" | "error";

class ModelService {
  private status: ModelStatus = "loading";
  private error: string | null = null;
  private sessionId: string | null = null;

  async initializeModel() {
    try {
      this.status = "ready";
      this.error = null;
      return true;
    } catch (err) {
      console.error("Model initialization error:", err);
      this.status = "error";
      this.error = err instanceof Error ? err.message : "Failed to initialize model";
      toast.error("Failed to initialize AI model: " + this.error);
      return false;
    }
  }

  getStatus(): { status: ModelStatus; error: string | null } {
    return { status: this.status, error: this.error };
  }

  async generateResponse(question: string, context: string = ''): Promise<string> {
    try {
      const { data, error } = await supabase.functions.invoke('gemini-agent', {
        body: { 
          task: 'answer_question',
          context,
          data: { question },
          sessionId: this.sessionId
        }
      });

      if (error) throw error;
      if (!data.response) throw new Error('No response received from model');

      // Store the session ID for future requests
      if (data.sessionId) {
        this.sessionId = data.sessionId;
      }

      return data.response;
    } catch (error) {
      console.error("Generation error:", error);
      throw error;
    }
  }

  async generateInterviewSummary(interviewId: string): Promise<void> {
    try {
      console.log(`[ModelService] Requesting summary generation for interview: ${interviewId}`);
      
      // First, check if the interview exists and get its content
      const { data: interview, error: fetchError } = await supabase
        .from('interviews')
        .select('id, answer, question, context, summary_status')
        .eq('id', interviewId)
        .single();
        
      if (fetchError) {
        console.error("[ModelService] Error fetching interview:", fetchError);
        throw new Error(`Interview not found: ${fetchError.message}`);
      }
      
      console.log(`[ModelService] Interview found with status: ${interview.summary_status}`);
      
      // Update the status to generating
      const { error: updateError } = await supabase
        .from('interviews')
        .update({ summary_status: 'generating' })
        .eq('id', interviewId);
        
      if (updateError) {
        console.error("[ModelService] Error updating interview status:", updateError);
        throw new Error(`Failed to update interview status: ${updateError.message}`);
      }
      
      // Prepare the full article context for summarization
      const fullContext = `Spørsmål: ${interview.question}\n\nSvar: ${interview.answer}\n\nKontekst: ${interview.context || ''}`;
      console.log(`[ModelService] Calling gemini-agent with context length: ${fullContext.length} chars`);
      
      // Call the gemini-agent function to generate summary
      const { data, error } = await supabase.functions.invoke('gemini-agent', {
        body: { 
          task: 'summarize_article',
          context: fullContext,
          data: { interviewId }
        }
      });

      console.log(`[ModelService] Gemini response:`, { data, error });

      if (error) {
        console.error("[ModelService] Error invoking gemini-agent for summary:", error);
        // Update status back to pending on error for retry
        await supabase
          .from('interviews')
          .update({ 
            summary_status: 'pending',
            summary_error: error.message || 'Failed to generate summary'
          })
          .eq('id', interviewId);
        throw error;
      }
      
      // Update the interview with the generated summary
      if (data && (data.summary || data.summaryText)) {
        // Use summaryText if available (newline-separated string), otherwise convert array to JSON string
        const summaryToStore = data.summaryText || 
          (Array.isArray(data.summary) ? JSON.stringify(data.summary) : data.summary);
        
        // Validation: check minimum length and completeness
        if (!summaryToStore || summaryToStore.length < 20) {
          console.error(`[ModelService] ❌ Summary too short or empty:`, summaryToStore);
          await supabase
            .from('interviews')
            .update({ 
              summary_status: 'error',
              summary_error: `Summary was too short or empty (length: ${summaryToStore?.length || 0})`
            })
            .eq('id', interviewId);
          throw new Error(`Summary was too short or empty`);
        }
        
        // Validation: check if summary appears truncated (no sentence-ending punctuation)
        const points = summaryToStore.split('\n').filter((p: string) => p.trim().length > 0);
        const allTruncated = points.every((p: string) => {
          const lastChar = p.trim().charAt(p.trim().length - 1);
          return !['.', '!', '?', ')'].includes(lastChar);
        });
        
        if (allTruncated && points.length < 3) {
          console.error(`[ModelService] ❌ Summary appears truncated:`, summaryToStore);
          await supabase
            .from('interviews')
            .update({ 
              summary_status: 'error',
              summary_error: `Summary appears truncated (${points.length} incomplete point(s))`
            })
            .eq('id', interviewId);
          throw new Error(`Summary appears truncated`);
        }
        
        console.log(`[ModelService] ✅ Valid summary, updating DB:`, summaryToStore.substring(0, 100) + '...');
        const { error: summaryUpdateError } = await supabase
          .from('interviews')
          .update({ 
            ai_summary: summaryToStore,
            summary_status: 'completed'
          })
          .eq('id', interviewId);
          
        if (summaryUpdateError) {
          console.error("[ModelService] Error updating summary:", summaryUpdateError);
          throw summaryUpdateError;
        }
        
        console.log(`[ModelService] Successfully generated and saved summary for interview ${interviewId}`);
        toast.success('Oppsummering generert');
      } else if (data && data.error) {
        console.error("[ModelService] AI returned error:", data.error);
        await supabase
          .from('interviews')
          .update({ 
            summary_status: 'error',
            summary_error: data.error
          })
          .eq('id', interviewId);
        throw new Error(data.error);
      } else {
        console.error("[ModelService] No summary in response:", data);
        await supabase
          .from('interviews')
          .update({ 
            summary_status: 'error',
            summary_error: 'No summary received from AI'
          })
          .eq('id', interviewId);
        throw new Error('No summary received from AI');
      }
    } catch (error) {
      console.error("[ModelService] Summary generation error:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if this is a content error (already set to 'error') or a network error
      const { data: currentInterview } = await supabase
        .from('interviews')
        .select('summary_status')
        .eq('id', interviewId)
        .single();
      
      // Only reset to 'pending' if this is a network/API error (not a content validation error)
      // Content errors are already marked as 'error' above
      if (currentInterview?.summary_status !== 'error') {
        try {
          await supabase
            .from('interviews')
            .update({ 
              summary_status: 'pending',
              summary_error: errorMessage
            })
            .eq('id', interviewId);
        } catch (updateError) {
          console.error("[ModelService] Failed to update error status:", updateError);
        }
      }
      
      toast.error('Kunne ikke generere oppsummering');
      throw error;
    }
  }
}

export const modelService = new ModelService();
